import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import DashboardHeader from '../components/common/DashboardHeader';
import axios from 'axios';
import { 
  Box, 
  Grid, 
  Paper, 
  Typography, 
  Badge,
  LinearProgress,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import ShareIcon from '@mui/icons-material/Share';
import LinkIcon from '@mui/icons-material/Link';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import BlockIcon from '@mui/icons-material/Block';
import HotelIcon from '@mui/icons-material/Hotel';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import IconButton from '@mui/material/IconButton';
import { detectThemeSync, watchThemeChanges } from '../utils/themeUtils';
import { useNavigate, useLocation } from 'react-router-dom';
import NotchTabs from '../components/common/NotchTabs';
import { isMobileShellLayout } from '../utils/mobileShellLayout';
import {
  getDashboardPageShellSx,
  getDashboardSectionHeaderDividerSx,
  getDashboardSectionNavButtonSx,
  getDashboardSectionTabCardSx,
} from '../utils/dashboardSurfaceSx';
import { getDistributionContentPaperSx } from '../utils/distributionMobileLayout';
import ChannelSetupSection from '../components/channels/ChannelSetupSection';
import ChannelInventorySection from '../components/channels/ChannelInventorySection';
import ChannelMappingSection from '../components/channels/ChannelMappingSection';
import InventorySection from '../components/channels/InventorySection';
import InventoryBlockSection from '../components/channels/InventoryBlockSection';
import NexaStaySection from '../nexastay/NexaStaySection';
import { PERMISSIONS } from '../auth/permissions';
import usePermission from '../auth/usePermission';

// Theme color constant
const THEME_COLOR = '#4ce1b6';

/** Stable reference — must not be recreated each render (would retrigger scroll sync effect). */
const DISTRIBUTION_SECTIONS = [
  {
    id: 'channel-setup',
    title: 'Channel Setup',
    shortLabel: 'Setup',
    icon: SettingsIcon,
    status: 'pending',
    progress: 0,
    count: null,
    url: '/distribution/channel-setup/',
  },
  {
    id: 'channel-inventory',
    title: 'Channel Inventory',
    shortLabel: 'Ch. Inv',
    icon: ShareIcon,
    status: 'pending',
    progress: 0,
    count: null,
    url: '/distribution/channel-inventory/',
  },
  {
    id: 'channel-mapping',
    title: 'Channel Mapping',
    shortLabel: 'Mapping',
    icon: LinkIcon,
    status: 'pending',
    progress: 0,
    count: null,
    url: '/distribution/channel-mapping/',
  },
  {
    id: 'pms-inventory',
    title: 'Inventory',
    shortLabel: 'PMS',
    icon: CloudSyncIcon,
    status: 'pending',
    progress: 0,
    count: null,
    url: '/distribution/pms-inventory/',
  },
  {
    id: 'inventory-block',
    title: 'Inventory Block',
    shortLabel: 'Block',
    icon: BlockIcon,
    status: 'pending',
    progress: 0,
    count: null,
    url: '/distribution/inventory-block/',
  },
  {
    id: 'nexastay',
    title: 'NexaStay',
    shortLabel: 'Stay',
    icon: HotelIcon,
    status: 'pending',
    progress: 0,
    count: null,
    url: '/distribution/nexastay/',
  },
];

const getDistributionSectionPermissionCodes = (sectionId) => {
  switch (sectionId) {
    case 'channel-setup':
    case 'channel-inventory':
    case 'channel-mapping':
      return [PERMISSIONS.DISTRIBUTION_MANAGE];
    case 'pms-inventory':
    case 'inventory-block':
      return [
        PERMISSIONS.DISTRIBUTION_MANAGE,
        PERMISSIONS.INVENTORY_MANAGE,
        PERMISSIONS.BOOKING_OPERATIONS,
      ];
    case 'nexastay':
      return [PERMISSIONS.DISTRIBUTION_MANAGE, PERMISSIONS.BOOKING_ENGINE_MANAGE];
    default:
      return [PERMISSIONS.DISTRIBUTION_MANAGE];
  }
};

const DistributionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { can } = usePermission();
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const isDark = currentTheme === 'dark';
  const isMobileLayout = isMobileShellLayout();
  const visibleSections = useMemo(
    () => DISTRIBUTION_SECTIONS.filter((section) => (
      getDistributionSectionPermissionCodes(section.id).some((permissionCode) => can(permissionCode))
    )),
    [can],
  );
  
  // Determine active section from URL
  const getActiveSectionFromPath = () => {
    if (location.pathname.includes('/nexastay')) return 'nexastay';
    if (location.pathname.includes('/channel-setup')) return 'channel-setup';
    if (location.pathname.includes('/channel-inventory')) return 'channel-inventory';
    if (location.pathname.includes('/channel-mapping')) return 'channel-mapping';
    if (location.pathname.includes('/pms-inventory')) return 'pms-inventory';
    if (location.pathname.includes('/inventory-block')) return 'inventory-block';
    return 'channel-setup';
  };
  
  const [activeSection, setActiveSection] = useState(getActiveSectionFromPath());
  const cardsContainerRef = useRef(null);
  const [sectionsData, setSectionsData] = useState({});
  const [loading, setLoading] = useState(true);
  /** Carousel arrows: scroll strip when possible; otherwise prev/next section (wide layouts). */
  const [carouselScroll, setCarouselScroll] = useState({ canLeft: false, canRight: false });

  // Update active section when route changes
  useEffect(() => {
    setActiveSection(getActiveSectionFromPath());
  }, [location.pathname]);

  // Scroll to active card when section changes (deps must not include a new [] each render)
  useEffect(() => {
    if (cardsContainerRef.current) {
      const activeIndex = visibleSections.findIndex((s) => s.id === activeSection);
      if (activeIndex !== -1) {
        const cardWidth = 180 + 16; // card width + gap
        const scrollPosition = activeIndex * cardWidth;
        cardsContainerRef.current.scrollTo({
          left: scrollPosition,
          behavior: 'smooth',
        });
      }
    }
  }, [activeSection, visibleSections]);

  const updateCarouselScrollState = useCallback(() => {
    const el = cardsContainerRef.current;
    if (!el) return;
    const idx = visibleSections.findIndex((s) => s.id === activeSection);
    const len = visibleSections.length;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const eps = 2;
    const atScrollEnd = scrollLeft + clientWidth >= scrollWidth - eps;
    setCarouselScroll({
      canLeft: !(scrollLeft <= eps && idx <= 0),
      canRight: !(atScrollEnd && idx >= len - 1),
    });
  }, [activeSection, visibleSections]);

  useEffect(() => {
    const t = window.setTimeout(() => updateCarouselScrollState(), 450);
    return () => clearTimeout(t);
  }, [activeSection, updateCarouselScrollState]);

  useEffect(() => {
    const el = cardsContainerRef.current;
    if (!el) return undefined;
    updateCarouselScrollState();
    const onScroll = () => updateCarouselScrollState();
    el.addEventListener('scroll', onScroll, { passive: true });
    const ro = new ResizeObserver(() => updateCarouselScrollState());
    ro.observe(el);
    window.addEventListener('resize', updateCarouselScrollState);
    return () => {
      el.removeEventListener('scroll', onScroll);
      ro.disconnect();
      window.removeEventListener('resize', updateCarouselScrollState);
    };
  }, [updateCarouselScrollState, loading, sectionsData]);

  // Watch for theme changes
  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => {
      setCurrentTheme(newTheme);
    });
    return cleanup;
  }, []);

  useEffect(() => {
    if (!visibleSections.length) return;
    if (visibleSections.some((section) => section.id === activeSection)) return;
    const fallbackSection = visibleSections[0];
    setActiveSection(fallbackSection.id);
    navigate(fallbackSection.url, { replace: true });
  }, [activeSection, navigate, visibleSections]);

  // Get CSRF token from cookies
  function getCookie(name) {
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.substring(0, name.length + 1) === (name + '=')) {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
          break;
        }
      }
    }
    return cookieValue;
  }

  // Fetch section data and calculate progress
  useEffect(() => {
    const fetchSectionData = async () => {
      setLoading(true);
      try {
        const csrftoken = getCookie('csrftoken');
        const headers = {
          'Accept': 'application/json',
          ...(csrftoken && { 'X-CSRFToken': csrftoken }),
        };

        const data = {};
        const canSeeSection = (sectionId) => visibleSections.some((section) => section.id === sectionId);

        // Fetch Channel Setup
        if (canSeeSection('channel-setup')) try {
          const channelsRes = await axios.get('/distribution/api/channels/', { withCredentials: true, headers });
          const count = channelsRes.data?.channels?.length || 0;
          data['channel-setup'] = {
            progress: count > 0 ? 100 : 0,
            status: count > 0 ? 'completed' : 'pending',
            count: count,
          };
        } catch (e) {
          data['channel-setup'] = { progress: 0, status: 'pending', count: 0 };
        }

        // Fetch Channel Inventory
        if (canSeeSection('channel-inventory')) try {
          const channelInventoriesRes = await axios.get('/distribution/api/channel-inventories/', { withCredentials: true, headers });
          const count = channelInventoriesRes.data?.inventories?.length || 0;
          data['channel-inventory'] = {
            progress: count > 0 ? 100 : 0,
            status: count > 0 ? 'completed' : 'pending',
            count: count,
          };
        } catch (e) {
          data['channel-inventory'] = { progress: 0, status: 'pending', count: 0 };
        }

        // Fetch Channel Mapping
        if (canSeeSection('channel-mapping')) try {
          const channelMappingsRes = await axios.get('/distribution/api/channel-mappings/', { withCredentials: true, headers });
          const count = channelMappingsRes.data?.mappings?.length || channelMappingsRes.data?.channel_mappings?.length || 0;
          data['channel-mapping'] = {
            progress: count > 0 ? 100 : 0,
            status: count > 0 ? 'completed' : 'pending',
            count: count,
          };
        } catch (e) {
          data['channel-mapping'] = { progress: 0, status: 'pending', count: 0 };
        }

        // Fetch PMS Inventory
        if (canSeeSection('pms-inventory')) try {
          const inventoriesRes = await axios.get('/distribution/api/inventories/', { withCredentials: true, headers });
          const count = inventoriesRes.data?.inventories?.length || 0;
          data['pms-inventory'] = {
            progress: count > 0 ? 100 : 0,
            status: count > 0 ? 'completed' : 'pending',
            count: count,
          };
        } catch (e) {
          data['pms-inventory'] = { progress: 0, status: 'pending', count: 0 };
        }

        // Fetch Inventory Block
        if (canSeeSection('inventory-block')) try {
          const inventoryBlocksRes = await axios.get('/distribution/api/inventory-blocks/', { withCredentials: true, headers });
          const count = inventoryBlocksRes.data?.blocks?.length || inventoryBlocksRes.data?.inventory_blocks?.length || 0;
          data['inventory-block'] = {
            progress: count > 0 ? 100 : 0,
            status: count > 0 ? 'completed' : 'pending',
            count: count,
          };
        } catch (e) {
          data['inventory-block'] = { progress: 0, status: 'pending', count: 0 };
        }

        setSectionsData(data);
      } catch (error) {} finally {
        setLoading(false);
      }
    };

    fetchSectionData();
  }, [visibleSections]);

  const handleSectionClick = (section) => {
    setActiveSection(section.id);
    navigate(section.url);
  };

  const getCurrentSectionIndex = () => {
    return visibleSections.findIndex((s) => s.id === activeSection);
  };

  const handlePrevious = () => {
    const currentIndex = getCurrentSectionIndex();
    if (currentIndex > 0) {
      const previousSection = visibleSections[currentIndex - 1];
      handleSectionClick(previousSection);
    }
  };

  const handleNext = () => {
    const currentIndex = getCurrentSectionIndex();
    if (currentIndex < visibleSections.length - 1) {
      const nextSection = visibleSections[currentIndex + 1];
      handleSectionClick(nextSection);
    }
  };

  const CARD_STRIP_STEP = 180 + 16;

  /** Prefer scrolling the strip; if already at strip start, go to previous section. */
  const handleCarouselArrowLeft = () => {
    const el = cardsContainerRef.current;
    if (!el) return;
    const eps = 2;
    const { scrollLeft } = el;
    if (scrollLeft > eps) {
      el.scrollBy({ left: -CARD_STRIP_STEP, behavior: 'smooth' });
    } else {
      const idx = getCurrentSectionIndex();
      if (idx > 0) handleSectionClick(visibleSections[idx - 1]);
    }
  };

  /** Prefer scrolling the strip; if already at strip end, go to next section. */
  const handleCarouselArrowRight = () => {
    const el = cardsContainerRef.current;
    if (!el) return;
    const eps = 2;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - eps;
    if (!atEnd) {
      el.scrollBy({ left: CARD_STRIP_STEP, behavior: 'smooth' });
    } else {
      const idx = getCurrentSectionIndex();
      if (idx >= 0 && idx < visibleSections.length - 1) {
        handleSectionClick(visibleSections[idx + 1]);
      }
    }
  };

  const currentIndex = getCurrentSectionIndex();
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < visibleSections.length - 1;
  const { canLeft: canScrollCarouselLeft, canRight: canScrollCarouselRight } = carouselScroll;

  const activeTabIndex = useMemo(
    () => Math.max(0, visibleSections.findIndex((s) => s.id === activeSection)),
    [activeSection, visibleSections],
  );

  const distributionNotchTabs = useMemo(
    () => visibleSections.map(({ icon, title, shortLabel }) => ({
      icon,
      label: title,
      shortLabel,
    })),
    [visibleSections],
  );

  const cardColor = THEME_COLOR;
  const progressColor = THEME_COLOR;

  return (
    <Box sx={getDashboardPageShellSx(currentTheme)}>
      <Box sx={{ mb: isMobileLayout ? 1 : 3 }}>
          <DashboardHeader
            title="Distribution"
            forceDark={isMobileLayout}
            navLinks={isMobileLayout ? [] : [
              { label: 'Dynamic Pricing', url: '/dynamic-pricing/' },
              { label: 'Configuration', url: '/configuration/' }
            ]}
            breadcrumbs={[
              { label: 'Home', path: '/dashboard/' },
              { label: 'Distribution' }
            ]}
          />
      </Box>

      {isMobileLayout ? (
        <Box sx={{ mb: 1.5 }}>
          <NotchTabs
            value={activeTabIndex}
            onChange={(_, index) => handleSectionClick(visibleSections[index])}
            tabs={distributionNotchTabs}
            accentColor={THEME_COLOR}
            sx={{ width: '100%', m: 0 }}
          />
        </Box>
      ) : (
      <Box
        sx={{
          position: 'relative',
          mb: 3,
          display: 'flex',
          alignItems: 'stretch',
        }}
      >
        {/* Left Arrow - Full Height */}
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 48,
            backgroundColor: 'transparent !important',
            zIndex: 10,
            cursor: canScrollCarouselLeft ? 'pointer' : 'not-allowed',
            opacity: canScrollCarouselLeft ? 1 : 0.3,
          }}
          onClick={canScrollCarouselLeft ? handleCarouselArrowLeft : undefined}
        >
          <IconButton
            disabled={!canScrollCarouselLeft}
            sx={{
              color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.54)',
              backgroundColor: 'transparent',
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
        </Box>

        {/* Cards Container */}
        <Box
          ref={cardsContainerRef}
          sx={{
            display: 'flex',
            gap: 2,
            overflowX: 'auto',
            overflowY: 'hidden',
            pt: 2,
            pb: 2,
            flex: 1,
            minWidth: 0,
            scrollbarWidth: 'none', // Firefox
            msOverflowStyle: 'none', // IE and Edge
            '&::-webkit-scrollbar': {
              display: 'none', // Chrome, Safari, Opera
            },
            scrollBehavior: 'smooth',
          }}
        >
        {visibleSections.map((section) => {
          const IconComponent = section.icon;
          const isActive = activeSection === section.id;
          const sectionData = sectionsData[section.id] || {};
          const sectionProgress = sectionData.progress !== undefined ? sectionData.progress : section.progress;
          const sectionStatus = sectionData.status || section.status;
          const sectionCount = sectionData.count !== undefined ? sectionData.count : section.count;

          return (
            <Paper
              key={section.id}
              elevation={0}
              onClick={() => handleSectionClick(section)}
              sx={getDashboardSectionTabCardSx(isDark, isActive, cardColor)}
            >
              {/* Badge for count */}
              {sectionCount !== null && sectionCount !== undefined && (
                <Badge
                  badgeContent={sectionCount}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    '& .MuiBadge-badge': {
                      backgroundColor: THEME_COLOR,
                      color: '#ffffff',
                      fontSize: '12px',
                      fontWeight: 600,
                      minWidth: '24px',
                      height: '24px',
                      borderRadius: '12px',
                    },
                  }}
                />
              )}

              {/* Icon */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: '50%',
                    backgroundColor: isActive
                      ? isDark
                        ? 'rgba(76, 225, 182, 0.2)'
                        : 'rgba(76, 225, 182, 0.1)'
                      : isDark
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(0, 0, 0, 0.02)',
                    transition: 'all 0.3s ease',
                  }}
                >
                  <IconComponent
                    sx={{
                      fontSize: 40,
                      color: isActive ? THEME_COLOR : (isDark ? '#e2e8f0' : '#666'),
                      transition: 'all 0.3s ease',
                    }}
                  />
                </Box>
              </Box>

              {/* Title */}
              <Typography
                variant="h6"
                sx={{
                  fontWeight: isActive ? 700 : 600,
                  color: isActive
                    ? THEME_COLOR
                    : isDark
                    ? '#e2e8f0'
                    : '#212121',
                  textAlign: 'center',
                  mb: 2,
                  fontSize: '16px',
                  transition: 'all 0.3s ease',
                }}
              >
                {section.title}
              </Typography>

              {/* Progress Bar */}
              <Box sx={{ mt: 'auto' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: isActive
                        ? isDark
                          ? '#f8fafc'
                          : THEME_COLOR
                        : isDark
                        ? 'rgba(148, 163, 184, 0.92)'
                        : '#757575',
                      fontWeight: isActive ? 600 : 400,
                      fontSize: '0.75rem',
                    }}
                  >
                    {sectionStatus === 'completed' ? 'Completed' : sectionStatus === 'active' ? 'In Progress' : 'Pending'}
                  </Typography>
                  {sectionCount !== null && sectionCount !== undefined && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: isActive
                          ? THEME_COLOR
                          : isDark
                          ? 'rgba(148, 163, 184, 0.92)'
                          : '#757575',
                        fontWeight: isActive ? 600 : 400,
                        fontSize: '0.75rem',
                      }}
                    >
                      {sectionCount} items
                    </Typography>
                  )}
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={sectionProgress}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: isActive
                      ? isDark
                        ? 'rgba(76, 225, 182, 0.1)'
                        : 'rgba(76, 225, 182, 0.1)'
                      : isDark
                      ? 'rgba(255,255,255,0.08)'
                      : '#e0e0e0',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: sectionProgress > 0 ? THEME_COLOR : (isDark ? '#333' : '#bdbdbd'),
                      borderRadius: 4,
                      transition: 'all 0.3s ease',
                    },
                  }}
                />
              </Box>
            </Paper>
          );
        })}
        </Box>

        {/* Right Arrow - Full Height */}
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 48,
            backgroundColor: 'transparent !important',
            zIndex: 10,
            cursor: canScrollCarouselRight ? 'pointer' : 'not-allowed',
            opacity: canScrollCarouselRight ? 1 : 0.3,
          }}
          onClick={canScrollCarouselRight ? handleCarouselArrowRight : undefined}
        >
          <IconButton
            disabled={!canScrollCarouselRight}
            sx={{
              color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.54)',
              backgroundColor: 'transparent',
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Box>
      )}

      {/* Content Area - Show content based on active section */}
      <Paper
        elevation={0}
        sx={{
          ...getDistributionContentPaperSx(isDark, isMobileLayout),
          position: 'relative',
        }}
      >
        {!isMobileLayout && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            pb: 2,
            borderBottom: getDashboardSectionHeaderDividerSx(isDark),
          }}
        >
          <IconButton
            onClick={handlePrevious}
            disabled={!hasPrevious}
            sx={getDashboardSectionNavButtonSx(isDark, hasPrevious, THEME_COLOR)}
          >
            <ChevronLeftIcon />
          </IconButton>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: isDark ? '#f8fafc' : '#212121',
              flexGrow: 1,
              textAlign: 'center',
            }}
          >
            {visibleSections.find((s) => s.id === activeSection)?.title || 'Distribution'}
          </Typography>

          <IconButton
            onClick={handleNext}
            disabled={!hasNext}
            sx={getDashboardSectionNavButtonSx(isDark, hasNext, THEME_COLOR)}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
        )}

        {isMobileLayout && (
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              color: '#f8fafc',
              mb: 1.5,
              textAlign: 'center',
            }}
          >
            {visibleSections.find((s) => s.id === activeSection)?.title || 'Distribution'}
          </Typography>
        )}

        {/* Section Content */}
        {activeSection === 'channel-setup' ? (
          <ChannelSetupSection />
        ) : activeSection === 'channel-inventory' ? (
          <ChannelInventorySection />
        ) : activeSection === 'channel-mapping' ? (
          <ChannelMappingSection />
        ) : activeSection === 'pms-inventory' ? (
          <InventorySection />
        ) : activeSection === 'inventory-block' ? (
          <InventoryBlockSection />
        ) : activeSection === 'nexastay' ? (
          <NexaStaySection />
        ) : (
          <Box>
            <Typography
              variant="body1"
              sx={{
                color: isDark ? '#999' : '#757575',
                textAlign: 'center',
                py: 8,
              }}
            >
              {`${visibleSections.find((s) => s.id === activeSection)?.title} content will be displayed here.`}
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default DistributionPage;

